import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Modal,
  Button,
  Alert,
  Spinner,
  Form,
  ButtonGroup,
} from 'react-bootstrap'
import { QrReader } from 'react-qr-reader_multiformatreader'
import { isEmptyStr } from '../../utils/objectutils'
import { isEmptyGuid } from '../../utils/emptyGuid'
import { useTranslation } from 'react-i18next'

const ViewFinder = () => (
  <div
    style={{
      top: '0px',
      left: '0px',
      zIndex: '1',
      boxSizing: 'border-box',
      border: '50px solid rgba(0, 0, 0, 0.3)',
      boxShadow: 'rgba(255, 0, 0, 0.5) 0px 0px 0px 5px inset',
      position: 'absolute',
      width: '100%',
      height: ' 100%',
    }}
  ></div>
)

const RFID_STATUS = {
  READY: 'READY',
  SCANNING: 'SCANNING',
  SENDING: 'SENDING',
}

const QrModal = (props) => {
  const {
    handleResult,
    handleClose,
    qrModalError,
    qrModalSuccess,
    qrModalInfo,
    scannedArticle,
    title,
    isLoading,
    children,
    rfidNotFoundList = [],
    rfidDuplicateList = [],
    rfidSuccess = false,
    setRfidSuccess = () => {},
    setRfidNotFoundList = () => {},
    setRfidDuplicateList = () => {},
  } = props

  const { t } = useTranslation()

  const [error, setError] = useState('')
  const [barcode, setBarcode] = useState('')
  const [toggleSwitch, setToggleSwitch] = useState(false)
  const [rfidScans, setRfidScans] = useState([])
  const [uniqueRfidScans, setUniqueRfidScans] = useState([])
  const [rfidStatus, setRfidStatus] = useState(RFID_STATUS.SCANNING)
  const [isSending, setIsSending] = useState(false)
  const inputRef = useRef(null)
  const scanTimeoutRef = useRef(null)
  const qrErrorShownRef = useRef(false)

  const focusInput = useCallback(() => {
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus()
      }
    }, 50)
  }, [])

  useEffect(() => {
    if (toggleSwitch && inputRef.current) {
      inputRef.current.focus()
    }
  }, [toggleSwitch])

  const validateRfidFormat = (rfid) => {
    if (!rfid) return false

    const alphanumeric = /^[0-9A-Z]+$/i

    const clean = rfid.trim()
    if (!alphanumeric.test(clean)) return false

    const validLengths = [8, 14, 16, 24, 32]

    return validLengths.includes(clean.length)
  }

  const addRfidScan = (code) => {
    if (isEmptyStr(code)) return

    if (!validateRfidFormat(code)) {
      setRfidNotFoundList((prev) => [...prev, code])
      return
    }

    setRfidScans((prev) => [...prev, code])

    setUniqueRfidScans((prev) => {
      if (!prev.includes(code)) {
        return [...prev, code]
      }
      return prev
    })
  }

  const sendRfidsInBatches = useCallback(
    async (rfids) => {
      if (!rfids || rfids.length === 0) return

      const batchSize = 10
      const failedRfids = []

      setRfidStatus(RFID_STATUS.SENDING)
      setIsSending(true)

      for (let i = 0; i < rfids.length; i += batchSize) {
        const batch = rfids.slice(i, i + batchSize)

        for (const rfid of batch) {
          try {
            const result = await handleResult(rfid)
            if (!result) failedRfids.push(rfid)
          } catch (err) {
            failedRfids.push(rfid)
          }
        }

        await new Promise((resolve) => setTimeout(resolve, 300))
      }

      setRfidStatus(RFID_STATUS.READY)
      setIsSending(false)
      focusInput()
    },
    [handleResult, focusInput]
  )

  useEffect(() => {
    if (uniqueRfidScans.length > 0) {
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current)
      }

      scanTimeoutRef.current = setTimeout(() => {
        sendRfidsInBatches(uniqueRfidScans)
        setUniqueRfidScans([])
        scanTimeoutRef.current = null
      }, 2000)
    }

    return () => {
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current)
        scanTimeoutRef.current = null
      }
    }
  }, [sendRfidsInBatches, uniqueRfidScans])

  const handleOnChange = (e) => {
    let code = e.target.value
    if (!isEmptyStr(code) && code.includes('&CTRLIN')) {
      handleClearBarcode(code)
      return
    }
    setBarcode(code)
  }

  const handleOnKeyDown = (e) => {
    if (e.key !== 'Enter') return

    if (toggleSwitch) {
      setRfidStatus(RFID_STATUS.SCANNING)
      setRfidDuplicateList([])
      setRfidNotFoundList([])
      setRfidSuccess(false)
    }

    e.preventDefault()
    const code = e.target.value

    if (toggleSwitch) {
      addRfidScan(code)
      setBarcode('')
    } else {
      handleClearBarcode(code)
    }
  }

  const handleClearBarcode = (code) => {
    if (isEmptyStr(code)) return

    let cleanCode = code

    if (cleanCode.includes(' &')) {
      const index = cleanCode.indexOf(' &')
      cleanCode = cleanCode.substring(0, index)
    }

    handleResult(cleanCode)
    setBarcode('')
  }

  const getRfidStatusVariant = () => {
    if (rfidStatus === RFID_STATUS.READY) return 'success'
    if (rfidStatus === RFID_STATUS.SCANNING) return 'warning'
    if (rfidStatus === RFID_STATUS.SENDING) return 'danger'
    return 'secondary'
  }
  const getRfidStatusLabel = () => {
    if (rfidStatus === RFID_STATUS.READY) return t('inventory:statusReady')
    if (rfidStatus === RFID_STATUS.SCANNING)
      return t('inventory:statusScanning')
    if (rfidStatus === RFID_STATUS.SENDING) return t('inventory:statusSending')
    return ''
  }

  return (
    <Modal show={true} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!toggleSwitch ? (
          error ? (
            <Alert variant='danger'>{error}</Alert>
          ) : isLoading ? (
            <div className='d-flex align-items-center justify-content-center'>
              <Spinner animation='border' variant='primary' />
            </div>
          ) : (
            <QrReader
              videoId='video'
              scanDelay={500}
              ViewFinder={ViewFinder}
              onResult={(result, error) => {
                if (result) {
                  handleResult(result.text)
                }

                if (error) {
                  if (!qrErrorShownRef.current) {
                    setError(t('orderTemplates:qrScannerError'))
                    qrErrorShownRef.current = true
                  }
                }
              }}
            />
          )
        ) : null}
        {
          <Form.Group>
            <ButtonGroup className='mt-2 mb-3 w-100'>
              <Button
                variant={!toggleSwitch ? 'primary' : 'outline-primary'}
                onClick={() => setToggleSwitch(false)}
              >
                {t('inventory:camera')}
              </Button>

              <Button
                variant={toggleSwitch ? 'primary' : 'outline-primary'}
                onClick={() => setToggleSwitch(true)}
              >
                {t('inventory:externalScanner')}
              </Button>
            </ButtonGroup>

            {isLoading ? (
              <div className='d-flex align-items-center justify-content-center'>
                <Spinner animation='border' variant='primary' />
              </div>
            ) : toggleSwitch ? (
              <Form.Control
                ref={inputRef}
                id='rfid'
                name='rfid'
                placeholder={t('inventory:scanCode')}
                value={barcode}
                onChange={handleOnChange}
                onKeyDown={handleOnKeyDown}
                disabled={isLoading || isSending}
                autoFocus
              />
            ) : (
              <Form.Control
                id='Barcode'
                name='Barcode'
                placeholder={t('inventory:barCode')}
                value={barcode}
                onChange={handleOnChange}
                onKeyDown={handleOnKeyDown}
                disabled={isLoading}
                autoFocus
              />
            )}
            {toggleSwitch && (
              <Alert className='mt-3' variant={getRfidStatusVariant()}>
                {getRfidStatusLabel()}
              </Alert>
            )}
          </Form.Group>
        }
        {children}
        {scannedArticle && !toggleSwitch && (
          <Alert variant='success' className='mt-3'>
            <h2>{t('inventory:positionFound')}:</h2>
            <ul>
              {scannedArticle.rfid ? (
                <li>
                  {t('inventory:scanCode')}: {scannedArticle.rfid}
                </li>
              ) : null}
              {scannedArticle.batchKey ? (
                <li>
                  {t('inventory:batchNumber')}: {scannedArticle.batchKey}
                </li>
              ) : null}
              {scannedArticle.serialKey ? (
                <li>
                  {t('inventory:serialNumber')}: {scannedArticle.serialKey}
                </li>
              ) : null}
              {scannedArticle.article ? (
                <li>
                  {t('productDetail:articleDescription')}:
                  {scannedArticle.article.displayName}
                  <br />
                  <small className='text-uppercase'>
                    {t('inventory:articleNr')}:
                    {scannedArticle.article.articleNumber}
                  </small>
                </li>
              ) : null}
              {scannedArticle.articleVariant &&
              !isEmptyGuid(scannedArticle.articleVariant?.id) ? (
                <li>
                  {t('size')}: {scannedArticle.articleVariant.displayName}
                </li>
              ) : null}
            </ul>
          </Alert>
        )}

        {toggleSwitch && (
          <Alert variant='info' className='mt-3'>
            <strong>
              {t('inventory:uniqueRfidScans')}
              {uniqueRfidScans.length}
            </strong>
            <ul>
              {uniqueRfidScans.map((scan, idx) => (
                <li key={idx}>{scan}</li>
              ))}
            </ul>
            <strong>
              {t('inventory:totalScans')}
              {rfidScans.length}
            </strong>
          </Alert>
        )}
        {qrModalError && !toggleSwitch ? (
          <Alert variant='danger'>{qrModalError}</Alert>
        ) : null}
        {qrModalSuccess && !toggleSwitch ? (
          <Alert variant='success'>{qrModalSuccess}</Alert>
        ) : null}
        {qrModalInfo && !toggleSwitch ? (
          <Alert variant='info'>{qrModalInfo}</Alert>
        ) : null}
        {rfidSuccess && toggleSwitch ? (
          <Alert variant='success'>{t('inventory:addedPosition')}</Alert>
        ) : null}
        {rfidNotFoundList.length > 0 && toggleSwitch && (
          <Alert variant='danger' className='mt-3'>
            {t('inventory:rfidNotFound')}
            {rfidNotFoundList.map((rfid, idx) => (
              <div key={idx}>{rfid}</div>
            ))}
          </Alert>
        )}
        {rfidDuplicateList.length > 0 && toggleSwitch && (
          <Alert variant='danger' className='mt-3'>
            {t('inventory:duplicateRfidFound')}
            {rfidDuplicateList.map((rfid) => (
              <div>{rfid}</div>
            ))}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={handleClose}>
          {t('closeButton')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default QrModal
